import type {
  Coverage,
  DecideTimeOff,
  LeaveBalance,
  RaiseTimeOff,
  TimeOffRequest,
} from "@dentacore/shared-types";

import { api } from "./client";

/**
 * Time off — STORY-096.
 *
 * **Clinic-scoped, not branch-scoped**, and the schema says so:
 * `time_off_requests` carries no branch. Somebody asking for Thursday off is
 * asking for all of Thursday, at every location they work.
 */
export function listTimeOff(status?: string, signal?: AbortSignal): Promise<TimeOffRequest[]> {
  const query = new URLSearchParams(status === undefined ? {} : { status });
  const suffix = query.toString() === "" ? "" : `?${query.toString()}`;
  return api<TimeOffRequest[]>(`/time-off${suffix}`, { signal });
}

/** Raise one. Omit `userId` to raise it for yourself. */
export function raiseTimeOff(body: RaiseTimeOff): Promise<TimeOffRequest> {
  return api<TimeOffRequest>("/time-off", { method: "POST", body });
}

/** Approve or decline. Refused when you filed it (S8) — the row says so first. */
export function decideTimeOff(
  requestId: string,
  what: "approve" | "decline",
  body: DecideTimeOff = {},
): Promise<TimeOffRequest> {
  return api<TimeOffRequest>(`/time-off/${requestId}/${what}`, { method: "POST", body });
}

/**
 * What approving this would leave behind — STORY-097, decision S9.
 *
 * A read, so the screen can show the warning before anybody presses anything.
 * The same check runs again on the server at the moment of approval: a report
 * from five minutes ago is not evidence about now.
 */
export function readCoverage(requestId: string, signal?: AbortSignal): Promise<Coverage> {
  return api<Coverage>(`/time-off/${requestId}/coverage`, { signal });
}

/** Withdraw your own request while it still waits (STORY-181a). Removed, not marked. */
export function withdrawTimeOff(requestId: string): Promise<void> {
  return api<void>(`/time-off/${requestId}/withdraw`, { method: "POST" });
}

/** Put a decided request back in the queue. The decision is cleared, not kept. */
export function reopenTimeOff(requestId: string): Promise<TimeOffRequest> {
  return api<TimeOffRequest>(`/time-off/${requestId}/reopen`, { method: "POST" });
}

/**
 * Everybody's leave balance for a year — STORY-098.
 *
 * **Annual leave only draws the allowance down** (S10), and the figures are
 * computed on every read: only the allowance is a row.
 */
export function listBalances(year?: number, signal?: AbortSignal): Promise<LeaveBalance[]> {
  const query = new URLSearchParams(year === undefined ? {} : { year: String(year) });
  const suffix = query.toString() === "" ? "" : `?${query.toString()}`;
  return api<LeaveBalance[]>(`/time-off/balances${suffix}`, { signal });
}
